/**
 * Create a tenant — one dialog, for the shell's org menu and the settings page.
 *
 * Both offered it, and both had written it out: the same title, the same
 * sentence, the same placeholder, the same "Creating…" button. What differed was
 * the half nobody reads twice — the shell POSTed `/organizations/{id}/tenants`
 * itself while settings went through `useTenancySource().createTenant`. So the
 * adapter's cache invalidation ran on one of the two paths, and the tenant list
 * behind the shell menu stayed stale after creating one from it. Two copies of a
 * form is a tidiness problem; two ways of calling the API is the bug it hides.
 *
 * The caller owns what happens NEXT (`onCreated`): the shell switches the
 * session into the new tenant and reloads, settings switches in place. Creating
 * it is the same act either way; being moved into it is not.
 */
import * as React from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  notifyError,
  notifySuccess,
} from "@authe/ui-core";
import { useTenancySource } from "@/api/adapters";

export interface CreateTenantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  /** The new tenant's id — the caller decides whether to switch into it. */
  onCreated: (tenantId: string | undefined) => void;
  /**
   * Suffix for the field's DOM id, so two mounted copies (the menu and the
   * settings page) never share one.
   */
  idPrefix?: string;
}

export function CreateTenantDialog({
  open,
  onOpenChange,
  orgId,
  onCreated,
  idPrefix = "new-tenant",
}: CreateTenantDialogProps): React.ReactElement {
  const source = useTenancySource();
  const [name, setName] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      const { id } = await source.createTenant(orgId, { name: trimmed });
      notifySuccess("Tenant created", { description: `Switching to “${trimmed}”…` });
      onOpenChange(false);
      setName("");
      onCreated(id);
    } catch (err) {
      notifyError(err, { title: "Could not create the tenant." });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Create tenant</DialogTitle>
            <DialogDescription>
              A tenant is an isolated workspace under your organization. You&apos;ll be switched into
              it once it&apos;s provisioned.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label htmlFor={`${idPrefix}-name`}>Tenant name</Label>
            <Input
              id={`${idPrefix}-name`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Acme Investigations"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || submitting}>
              {submitting ? "Creating…" : "Create tenant"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
